import React from 'react';
import _ from 'lodash';
import Big from 'big.js';

import {
  FormModal,
  TransferList
} from 'modules';
import {
  Button,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  useFieldArray,
  useForm
} from 'react-hook-form';

import * as dataService from './dataService';
import * as types from './types';
import { fields, validationUtil } from 'iron-forge-react-modules';



const ControlledTextField = fields.TextField.genControllerComponent<types.IFormState>();



export interface IItem {
  itemId: string;
  defaultPriceDollars: Big;
}



export interface IProps {
  selectedItemIds: string[];
  canBeManagedByStoreId?: string;
  onClose: () => void;
  onSubmit: (items: IItem[]) => void;
}

export const FC: React.FC<IProps> = props => {
  const [step, setStep] = React.useState<'selectItems' | 'setPrices'>('selectItems');
  const form = useForm<types.IFormState>({
    defaultValues: {
      items: []
    }
  });
  const fieldArray = useFieldArray<types.IFormState>({
    control: form.control,
    name: 'items'
  });

  const queryTuple = dataService.useQuery(props.canBeManagedByStoreId ?? '');
  const transferListState = TransferList.useTransferListState(props.selectedItemIds);



  const selectedItems = React.useMemo(
    () => queryTuple.data.items.filter(item => transferListState.id2IsSelected[item.id]),
    [
      queryTuple,
      transferListState
    ]
  );



  const processSubmit = React.useCallback(
    async (formState: types.IFormState) => {
      if (step === 'selectItems') {
        form.setValue('items', selectedItems);
        setStep('setPrices');

      } else {
        const itemsToSubmit = formState.items.map((item): IItem => ({
          itemId: item.itemId,
          defaultPriceDollars: new Big(item.defaultPriceDollars)
        }));

        if (_.isEmpty(itemsToSubmit)) {
          window.alert("Must select at least one item");

        } else if (
          itemsToSubmit.some(item => item.defaultPriceDollars.eq(0)) && window.confirm("At least 1 item has a price of $0. Are you sure you want this?") ||
          itemsToSubmit.every(item => item.defaultPriceDollars.gt(0)) && window.confirm("Are you sure you want to select these items and prices?")
        ) {
          props.onSubmit(itemsToSubmit);
        }
      }
    },
    [
      step,
      form,
      selectedItems,
      props
    ]
  );



  return (
    <FormModal.FC
      isDoubleWide
      title="Select Items"
      onClose={props.onClose}
      isLoading={queryTuple.loading}
      onSubmit={form.handleSubmit(processSubmit)}
      fnGenElAcceptButton={args => (
        <Button
          {...args}
          variant="contained"
          disabled={queryTuple.loading}
          type="submit">
          {step === 'selectItems' ? "Next" : "Submit"}
        </Button>
      )}>
      {step === 'selectItems' && (
        <TransferList.FC
          {...transferListState}
          allItems={queryTuple.data.items}
          loading={queryTuple.loading} />
      )}

      {step === 'setPrices' && (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableCell />
              <TableCell />
            </TableHead>

            <TableBody>
              {fieldArray.fields.map((field, i) => (
                <TableRow key={field.id}>
                  <TableCell>
                    <ListItemText
                      primary={field.primaryText}
                      secondary={field.secondaryText} />
                  </TableCell>

                  <TableCell>
                    <ControlledTextField
                      disableDefaultMargins
                      fieldKey={`items.${i}.defaultPriceDollars`}
                      formControl={form.control}
                      isRequired
                      extraValidationRules={{ pattern: validationUtil.reDecimalNumber }} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </FormModal.FC>
  );
};
